import { Grid, GridColumn as Column, GridCustomCellProps, GridDetailRowProps } from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchApi } from 'services/api';

type SalesCustomer = {
  CustomerID: number;
};

type SalesCustomerOrder = {
  OrderID: number;
  OrderNumber: number;
  Status: string;
  ReferenceNumber1: string;
  ShipDateTime: string;
  DeliveryDateTime: string;
  BookingUser: string;
  ShipperName: string;
  ShipperCity: string;
  ShipperState: string;
  ConsigneeName: string;
  ConsigneeCity: string;
  ConsigneeState: string;
  VehicleType: string;
  TotalCharges: number;
};

type Response = {
  Orders: SalesCustomerOrder[];
};

interface SalesCustomerOrdersDetailProps extends GridDetailRowProps {
  customerDateFilter: number;
  salespersonId?: string;
  useStandardCalendar?: boolean;
}

const formatDateTime = (value: string) => new Date(value).toLocaleString();

const SalesCustomerOrdersDetail = ({ dataItem, customerDateFilter, salespersonId, useStandardCalendar }: SalesCustomerOrdersDetailProps) => {
  const customer = dataItem as SalesCustomer;
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<SalesCustomerOrder[]>([]);

  useEffect(() => {
    const params = new URLSearchParams({
      customerDateFilter: customerDateFilter.toString(),
      useStandardCalendar: (useStandardCalendar ?? false).toString()
    });
    if (salespersonId) {
      params.set('salespersonId', salespersonId);
    }

    setLoading(true);
    fetchApi(`/api/Customer/SalespersonDashboardCustomerOrders/${customer.CustomerID}?${params.toString()}`)
      .then((response: Response) => setOrders(response.Orders))
      .catch(() => alert('Unable to load order details.'))
      .finally(() => setLoading(false));
  }, [customer.CustomerID, customerDateFilter, salespersonId, useStandardCalendar]);

  const OrderCell = (props: GridCustomCellProps) => {
    if (!props.field || props.rowType !== 'data') return null;
    const order = props.dataItem as SalesCustomerOrder;

    return (
      <td {...props.tdProps}>
        <Link style={{ color: '#007bff' }} to={`/Order/${order.OrderID}`}>{order.OrderNumber}</Link>
      </td>
    );
  };

  const LocationCell = (props: GridCustomCellProps, stopType: 'Shipper' | 'Consignee') => {
    if (props.rowType !== 'data') return null;
    const order = props.dataItem as SalesCustomerOrder;
    const name = stopType === 'Shipper' ? order.ShipperName : order.ConsigneeName;
    const city = stopType === 'Shipper' ? order.ShipperCity : order.ConsigneeCity;
    const state = stopType === 'Shipper' ? order.ShipperState : order.ConsigneeState;

    return (
      <td {...props.tdProps}>
        <div>{name}</div>
        <div>{city}, {state}</div>
      </td>
    );
  };

  if (loading) return <Loader type="pulsing" size="small" />;
  if (!orders.length) return <div className="k-text-center">No orders found for this customer in the selected timeframe.</div>;

  return (
    <Grid data={orders} dataItemKey="OrderID">
      <Column field="OrderNumber" title="Order #" cells={{ data: OrderCell }} />
      <Column field="Status" title="Status" />
      <Column field="ReferenceNumber1" title="Ref #" />
      <Column field="ShipDateTime" title="Ship Date" cells={{ data: (props) => <td {...props.tdProps}>{formatDateTime(props.dataItem.ShipDateTime)}</td> }} />
      <Column field="DeliveryDateTime" title="Delivery Date" cells={{ data: (props) => <td {...props.tdProps}>{formatDateTime(props.dataItem.DeliveryDateTime)}</td> }} />
      <Column title="Shipper" cells={{ data: (props) => LocationCell(props, 'Shipper') }} />
      <Column title="Consignee" cells={{ data: (props) => LocationCell(props, 'Consignee') }} />
      <Column field="VehicleType" title="Vehicle" />
      <Column field="BookingUser" title="Booked By" />
      <Column field="TotalCharges" title="Total Charges" format="{0:c}" />
    </Grid>
  );
};

export default SalesCustomerOrdersDetail;
